import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, DollarSign } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'emi_calculator_data';

export const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanAmount : 500000;
  });
  
  const [interestRate, setInterestRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).interestRate : 8.5;
  });
  
  const [loanTenure, setLoanTenure] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanTenure : 60;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      loanAmount,
      interestRate,
      loanTenure
    }));
  }, [loanAmount, interestRate, loanTenure]);

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      return {
        monthlyEMI: 0,
        totalInterest: 0,
        totalAmount: 0,
        principalAmount: principal
      };
    }

    const monthlyRate = rate / 12 / 100;
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenure) / (Math.pow(1 + monthlyRate, tenure) - 1);
    const totalAmount = emi * tenure;
    const totalInterest = totalAmount - principal;

    return {
      monthlyEMI: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      principalAmount: principal
    };
  };

  const generateAmortizationSchedule = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    if (principal <= 0 || rate <= 0 || tenure <= 0) return [];

    const monthlyRate = rate / 12 / 100;
    const emi = calculateEMI().monthlyEMI;
    let balance = principal;
    const schedule = [];

    const step = Math.max(1, Math.floor(tenure / 12));

    for (let month = 0; month <= tenure; month += step) {
      if (month === 0) {
        schedule.push({
          month: 0,
          emi: 0,
          principal: 0,
          interest: 0,
          balance: Math.round(principal)
        });
        continue;
      }

      const actualMonth = Math.min(month, tenure);
      balance = principal;
      let totalInterestPaid = 0;

      for (let m = 1; m <= actualMonth; m++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = emi - interestPayment;
        totalInterestPaid += interestPayment;
        balance -= principalPayment;
      }

      const totalPrincipalPaid = principal - balance;

      schedule.push({
        month: actualMonth,
        emi: Math.round(emi),
        principal: Math.round(totalPrincipalPaid),
        interest: Math.round(totalInterestPaid),
        balance: Math.round(Math.max(0, balance))
      });
    }

    return schedule;
  };

  const generateYearlyBreakdown = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    if (principal <= 0 || rate <= 0 || tenure <= 0) return [];

    const monthlyRate = rate / 12 / 100;
    const emi = calculateEMI().monthlyEMI;
    let balance = principal;
    const yearlyData = [];

    const years = Math.ceil(tenure / 12);

    for (let year = 1; year <= years; year++) {
      let yearlyPrincipal = 0;
      let yearlyInterest = 0;
      const monthsInYear = year === years ? tenure % 12 || 12 : 12;

      for (let month = 1; month <= monthsInYear; month++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = emi - interestPayment;
        yearlyInterest += interestPayment;
        yearlyPrincipal += principalPayment;
        balance -= principalPayment;
      }

      yearlyData.push({
        year: `Year ${year}`,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest)
      });
    }

    return yearlyData;
  };

  const generatePrepaymentImpact = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;

    if (principal <= 0 || rate <= 0 || tenure <= 0) return [];

    const prepaymentAmounts = [0, principal * 0.05, principal * 0.1, principal * 0.15, principal * 0.2];
    
    return prepaymentAmounts.map(prepayment => {
      const newPrincipal = principal - prepayment;
      const monthlyRate = rate / 12 / 100;
      const emi = newPrincipal * monthlyRate * Math.pow(1 + monthlyRate, tenure) / (Math.pow(1 + monthlyRate, tenure) - 1);
      const totalAmount = emi * tenure;
      const totalInterest = totalAmount - newPrincipal;

      return {
        prepayment: Math.round(prepayment),
        prepaymentPercent: `${Math.round(prepayment / principal * 100)}%`,
        totalInterest: Math.round(totalInterest),
        saving: Math.round(calculateEMI().totalInterest - totalInterest)
      };
    });
  };

  const results = calculateEMI();
  const amortizationSchedule = generateAmortizationSchedule();
  const yearlyBreakdown = generateYearlyBreakdown();
  const prepaymentImpact = generatePrepaymentImpact();

  const pieData = [
    { name: 'Principal', value: results.principalAmount, color: 'hsl(var(--chart-1))' },
    { name: 'Interest', value: results.totalInterest, color: 'hsl(var(--chart-2))' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  EMI Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Tips
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is EMI?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  EMI (Equated Monthly Installment) is a fixed payment amount made by a borrower to a lender at a specified 
                  date each month. EMIs are used to pay off both interest and principal each month so that over a specified 
                  tenure, the loan is paid off in full.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Loan Details</CardTitle>
                  <CardDescription>Enter your loan information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="loanAmount" className="text-sm font-medium">
                      Loan Amount ($)
                    </Label>
                    <Input
                      id="loanAmount"
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      placeholder="500000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Principal amount you wish to borrow</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-sm font-medium">
                      Interest Rate (% per annum)
                    </Label>
                    <Input
                      id="interestRate"
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      placeholder="8.5"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Annual interest rate charged by lender</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loanTenure" className="text-sm font-medium">
                      Loan Tenure (Months)
                    </Label>
                    <Input
                      id="loanTenure"
                      type="number"
                      value={loanTenure}
                      onChange={(e) => setLoanTenure(e.target.value)}
                      placeholder="60"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Duration in months (e.g., 60 = 5 years)</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setLoanAmount(loanAmount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate EMI
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">EMI Calculation Results</CardTitle>
                    <CardDescription>Your monthly payment breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly EMI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.monthlyEMI)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">per month for {loanTenure} months</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.totalInterest)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {((results.totalInterest / results.principalAmount) * 100).toFixed(1)}% of principal
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-accent-foreground" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Amount Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent-foreground">
                          {formatCurrency(results.totalAmount)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Principal + Interest</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Info className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Principal Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {formatCurrency(results.principalAmount)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Original loan amount</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <h3 className="text-lg font-display font-semibold mb-4">Quick Insights</h3>
                      <div className="grid sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground mb-1">Interest Rate</p>
                          <p className="text-xl font-bold text-foreground">{interestRate}% p.a.</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Loan Period</p>
                          <p className="text-xl font-bold text-foreground">{Math.round(loanTenure / 12)} years</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Total Payments</p>
                          <p className="text-xl font-bold text-foreground">{loanTenure} EMIs</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">EMI Breakdown (Principal vs Interest)</CardTitle>
                <CardDescription className="text-base">
                  Visual representation of how your total payment is split
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Outstanding Balance Over Time</CardTitle>
                  <CardDescription>
                    See how your loan balance decreases
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={amortizationSchedule}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="balance" 
                          stroke="hsl(var(--primary))" 
                          fill="hsl(var(--primary))"
                          fillOpacity={0.3}
                          name="Outstanding Balance"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Interest vs Principal (Yearly)</CardTitle>
                  <CardDescription>
                    Annual breakdown of payments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={yearlyBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="principal" stackId="a" fill="hsl(var(--chart-1))" name="Principal" />
                        <Bar dataKey="interest" stackId="a" fill="hsl(var(--chart-2))" name="Interest" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Prepayment Impact Analysis</CardTitle>
                <CardDescription>
                  See how making prepayments can save you money
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={prepaymentImpact}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="prepaymentPercent" 
                        label={{ value: 'Prepayment Amount', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="totalInterest" fill="hsl(var(--chart-2))" name="Total Interest" />
                      <Bar dataKey="saving" fill="hsl(var(--chart-3))" name="Interest Saved" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your EMI</CardTitle>
                <CardDescription className="text-base">
                  Tips and insights to manage your loan effectively
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    How is EMI Calculated?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your monthly EMI of <strong className="text-primary">{formatCurrency(results.monthlyEMI)}</strong> is 
                    calculated using the reducing balance method. The EMI formula considers your loan amount, interest rate, 
                    and tenure to determine a fixed monthly payment.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">EMI Formula:</p>
                    <p className="font-mono">EMI = P × r × (1 + r)^n / ((1 + r)^n - 1)</p>
                    <div className="mt-3 text-xs space-y-1">
                      <p>Where: P = Principal ({formatCurrency(results.principalAmount)})</p>
                      <p>r = Monthly interest rate ({(interestRate / 12).toFixed(3)}%)</p>
                      <p>n = Number of months ({loanTenure})</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Interest vs Principal Over Time
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    In the early months of your loan, a larger portion of your EMI goes toward interest. As the loan 
                    progresses, more of your payment goes toward reducing the principal. This is called the "reducing 
                    balance method."
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Early Payments</p>
                      <p className="text-lg font-bold text-primary mb-2">70-80% Interest</p>
                      <p className="text-sm text-muted-foreground">
                        Most of your EMI goes to interest
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Later Payments</p>
                      <p className="text-lg font-bold text-secondary mb-2">70-80% Principal</p>
                      <p className="text-sm text-muted-foreground">
                        More goes toward reducing the loan
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent-foreground">
                    Benefits of Making Prepayments
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Making prepayments can significantly reduce your total interest burden. Even small prepayments in 
                    the early years can lead to substantial savings.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">10% Prepayment</span>
                        <span className="text-lg font-bold text-secondary">
                          Save {formatCurrency(prepaymentImpact[2]?.saving || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">20% Prepayment</span>
                        <span className="text-lg font-bold text-secondary">
                          Save {formatCurrency(prepaymentImpact[4]?.saving || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Smart Loan Management Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Make Prepayments Early</h4>
                      <p className="text-muted-foreground">
                        Prepayments made in the initial years have the maximum impact on reducing total interest.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose Shorter Tenure</h4>
                      <p className="text-muted-foreground">
                        A shorter loan tenure means higher EMI but significantly lower total interest paid.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Compare Interest Rates</h4>
                      <p className="text-muted-foreground">
                        Even a 0.5% difference in interest rate can save lakhs over the loan tenure. Shop around!
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Maintain Good Credit Score</h4>
                      <p className="text-muted-foreground">
                        A higher credit score (750+) can help you negotiate better interest rates with lenders.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Processing Fees:</strong> Banks charge 1-2% of loan amount 
                        as processing fee. Factor this into your total cost.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Prepayment Charges:</strong> Some loans have prepayment 
                        penalties. Check the terms before making extra payments.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Fixed vs Floating Rate:</strong> Fixed rates offer 
                        stability while floating rates may be lower initially but can change.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Insurance:</strong> Consider loan insurance to protect 
                        your family in case of unforeseen circumstances.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EMICalculator;
